import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { X, ArrowRight, ArrowLeft, User, Phone, MapPin, FileText, CheckCircle, Clock, Calendar, Zap, RefreshCw, ChevronDown, Package, Plus, Send, Mail, MessageSquare, Save, Copy, Edit3, UserPlus } from 'lucide-react';
import QuoteSignatureModal from './QuoteSignatureModal';
import DatePicker from './DatePicker';
import VisualScheduler from './VisualScheduler';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';
import { useFeatures } from './FeatureContext';
import { nameError, phoneError, addressError, cityError, stateError } from './validators';
import { generateRecurringInstances } from './recurringJobUtils';
import { getTodayString, getLocalDateString } from './dateUtils';
import { insertWithSync, isOnline } from './offlineService';
import { isOfflineModeEnabled, getCachedData, cacheData } from './offlineCache';

export default function ExpressJobWorkflow({ companyId, onClose, onComplete, userRole, currentUserId, prefilledCustomer = null, isMobile, textingEnabled, initialWorkflowType = null }) {
  const { colors } = useTheme();
  const { showAlert, showConfirm, showSuccess, showError } = useModal();
  const { hasFeature } = useFeatures();

  // Determine if user needs to select a tech (office/admin don't have their own calendar)
  const needsTechSelection = userRole === 'office' || userRole === 'admin';
  const canSelfAssign = userRole === 'owner' || userRole === 'admin' || userRole === 'field_tech' || userRole === 'member';


  const [step, setStep] = useState(1); // 1: Customer, 2: Choose Workflow (skipped if initialWorkflowType), 3: Quote+Sign (full) OR Schedule (quick), 4: Schedule (full)
  const [workflowType, setWorkflowType] = useState(initialWorkflowType); // 'quick' (visit) or 'full' (job)
  const [partners, setPartners] = useState([]);
  const [skus, setSkus] = useState([]);
  const [pricingTables, setPricingTables] = useState([]);
  
  // Customer data
  const [customerData, setCustomerData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    email: '',
    source_partner_id: ''
  });

  // Quote data
  const [depositRequired, setDepositRequired] = useState(false);
  const [depositPercentage, setDepositPercentage] = useState(50);

  // Other quote data
  const [selectedPricingTable, setSelectedPricingTable] = useState(null);
  const [quoteItems, setQuoteItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [discount, setDiscount] = useState(0);
  
  // Created records
  const [createdCustomer, setCreatedCustomer] = useState(null);
  const [createdQuote, setCreatedQuote] = useState(null);
  const [showSignModal, setShowSignModal] = useState(false);

  // Saving state (prevents double-clicks)
  const [saving, setSaving] = useState(false);
  const [creatingJob, setCreatingJob] = useState(false);

  // Send quote modal state
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendingQuote, setSendingQuote] = useState(false);
  const [sendMethod, setSendMethod] = useState('email');
  const [quoteLinkCopied, setQuoteLinkCopied] = useState(false);
  const [sendModalEmail, setSendModalEmail] = useState('');
  const [sendModalPhone, setSendModalPhone] = useState('');
  const [editingContactInfo, setEditingContactInfo] = useState(false);
  const [additionalRecipients, setAdditionalRecipients] = useState([]);

  // Scheduling
  const [schedulingChoice, setSchedulingChoice] = useState(null); // 'now' or 'later'
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('9:00');
  const [estimatedDuration, setEstimatedDuration] = useState(60); // minutes
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Tech assignment (for office/admin who need to select a tech)
  const [technicians, setTechnicians] = useState([]);
  const [selectedTech, setSelectedTech] = useState('');

  // Recurring job options
  const [recurringEnabled, setRecurringEnabled] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [salesTaxMode, setSalesTaxMode] = useState('optional'); // 'required', 'optional', 'disabled'
  const [recurrenceRule, setRecurrenceRule] = useState('weekly');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [calendarSlotMinutes, setCalendarSlotMinutes] = useState(60); // 15, 30, or 60
  const [autoChargeEnabled, setAutoChargeEnabled] = useState(false);

  // Quote fee settings
  const [quoteFeeEnabled, setQuoteFeeEnabled] = useState(false);
  const [quoteFeeAmount, setQuoteFeeAmount] = useState(75);

  // Product dropdown state
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const productDropdownRef = useRef(null);

  // Close product dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
    };

    if (showProductDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProductDropdown]);

  useEffect(() => {
    loadData();
  }, []);

  // Handle prefilled customer - skip step 1 and go straight to workflow selection
  // (or straight to step 3 if the caller already pre-chose visit vs job)
  useEffect(() => {
    if (prefilledCustomer) {
      setCreatedCustomer(prefilledCustomer);
      setStep(initialWorkflowType ? 3 : 2);
    }
  }, [prefilledCustomer, initialWorkflowType]);

  useEffect(() => {
    if (scheduledDate && estimatedDuration) {
      checkAvailableSlots();
    }
  }, [scheduledDate, estimatedDuration, selectedTech]);

  const checkAvailableSlots = async () => {
    if (!scheduledDate) return;

    setLoadingSlots(true);
    try {
      const buffer = parseInt(localStorage.getItem('schedulingBuffer') || '30');
      const totalTimeNeeded = estimatedDuration + buffer; // Job duration + buffer

      // Fetch jobs for the selected date - filter by tech if one is selected
      let query = supabase
        .from('jobs')
        .select('scheduled_time, estimated_duration')
        .eq('scheduled_date', scheduledDate);

      // If a tech is selected, only look at their schedule for conflicts
      if (selectedTech) {
        query = query.eq('assigned_tech', selectedTech);
      }

      const { data: dayJobs, error } = await query.order('scheduled_time');
      
      if (error) throw error;
      
      // Generate all possible 15-min time slots for 24 hours
      const allSlots = [];
      for (let hour = 0; hour <= 23; hour++) {
        allSlots.push(`${hour}:00`);
        allSlots.push(`${hour}:15`);
        allSlots.push(`${hour}:30`);
        allSlots.push(`${hour}:45`);
      }
      
      // Check each slot to see if there's enough continuous free time
      const available = allSlots.filter(slot => {
        const slotMinutes = parseInt(slot.split(':')[0]) * 60 + parseInt(slot.split(':')[1] || '0');
        const endMinutes = slotMinutes + totalTimeNeeded;
        
        // Check if any existing job overlaps with this window
        const hasConflict = (dayJobs || []).some(job => {
          const jobTime = job.scheduled_time || '00:00:00';
          const jobMinutes = parseInt(jobTime.split(':')[0]) * 60 + parseInt(jobTime.split(':')[1]);
          const jobDuration = job.estimated_duration || 60;
          const jobBuffer = buffer;
          const jobEnd = jobMinutes + jobDuration + jobBuffer;
          
          // Check if job overlaps with our proposed window
          return (
            (jobMinutes >= slotMinutes && jobMinutes < endMinutes) || // Job starts during our window
            (jobEnd > slotMinutes && jobEnd <= endMinutes) || // Job ends during our window
            (jobMinutes <= slotMinutes && jobEnd >= endMinutes) // Job completely covers our window
          );
        });
        
        return !hasConflict;
      });
      
      setAvailableTimeSlots(available);
      
      // Auto-select first available slot if current selection is not available
      if (available.length > 0 && !available.includes(scheduledTime)) {
        setScheduledTime(available[0]);
      }
    } catch (error) {
    } finally {
      setLoadingSlots(false);
    }
  };

  const loadData = async () => {
    // Offline: serve from cache
    if (!navigator.onLine && isOfflineModeEnabled()) {
      const cachedSkus = getCachedData('skus', companyId);
      const cachedPricingTables = getCachedData('pricing_tables', companyId);
      if (cachedSkus) setSkus(cachedSkus);
      if (cachedPricingTables) setPricingTables(cachedPricingTables);
      return;
    }

    try {
      const [partnersData, skusData, tablesData, companyData] = await Promise.all([
        supabaseData.from('commission_partners').select('*'),
        supabaseData.from('skus').select('*'),
        supabaseData.from('pricing_tables').select('*'),
        supabaseData.from('companies').select('recurring_enabled, sales_tax_mode, calendar_slot_minutes, quote_fee_enabled, default_quote_fee_amount').eq('id', companyId).single()
      ]);

      if (partnersData.data) setPartners(partnersData.data);
      if (skusData.data) setSkus(skusData.data);
      if (tablesData.data) setPricingTables(tablesData.data);
      if (companyData.data) {
        setRecurringEnabled(companyData.data.recurring_enabled || false);
        setSalesTaxMode(companyData.data.sales_tax_mode || 'optional');
        setCalendarSlotMinutes(companyData.data.calendar_slot_minutes || 60);
        setQuoteFeeEnabled(companyData.data.quote_fee_enabled || false);
        setQuoteFeeAmount(companyData.data.default_quote_fee_amount || 75);
      }

      // Load technicians for assignment dropdown
      await loadTechnicians();

      // Cache on success
      if (isOfflineModeEnabled()) {
        if (skusData.data) cacheData('skus', companyId, skusData.data);
        if (tablesData.data) cacheData('pricing_tables', companyId, tablesData.data);
      }
    } catch (e) {
      // Fallback to cache
      if (isOfflineModeEnabled()) {
        const cachedSkus = getCachedData('skus', companyId);
        const cachedPricingTables = getCachedData('pricing_tables', companyId);
        if (cachedSkus) setSkus(cachedSkus);
        if (cachedPricingTables) setPricingTables(cachedPricingTables);
      }
    }
  };

  const loadTechnicians = async () => {
    try {
      // Get team members
      const { data: teamData } = await supabaseData
        .from('user_companies')
        .select('*')
        .eq('company_id', companyId);

      if (!teamData || teamData.length === 0) return;

      // Get user profiles
      const userIds = teamData.map(m => m.user_id);
      const { data: profiles } = await supabaseData
        .from('user_profiles')
        .select('id, full_name, email, role')
        .in('id', userIds);

      const profilesMap = {};
      (profiles || []).forEach(p => { profilesMap[p.id] = p; });

      // Build technicians list - all team members except office-only roles
      const techs = teamData
        .filter(member => {
          const companyRole = member.role;
          return companyRole === 'owner' || companyRole === 'admin' || companyRole === 'field_tech' || companyRole === 'member';
        })
        .map(member => {
          const profile = profilesMap[member.user_id];
          return {
            id: member.user_id,
            name: profile?.full_name || profile?.email?.split('@')[0] || 'Team Member',
            role: member.role
          };
        });

      setTechnicians(techs);

      // Auto-select self if user is a tech/owner
      if (canSelfAssign && currentUserId) {
        const selfInList = techs.find(t => t.id === currentUserId);
        if (selfInList) {
          setSelectedTech(currentUserId);
        }
      }
    } catch (err) {
      console.error('Failed to load technicians:', err);
    }
  };

  // Validation helpers
  const validatePhone = (phone) => {
    // Remove all non-numeric characters
    const digits = phone.replace(/\D/g, '');
    // Valid US phone numbers have 10 digits (or 11 with leading 1)
    return digits.length >= 10;
  };

  const validateEmail = (email) => {
    if (!email) return true; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateZip = (zip) => {
    if (!zip) return true; // Zip is optional
    const zipRegex = /^\d{5}(-\d{4})?$/;
    return zipRegex.test(zip);
  };

  const handleCustomerNext = async () => {
    // Collect all errors at once
    const errs = [];

    const firstErr = nameError(customerData.first_name, 'First name');
    if (firstErr) errs.push(firstErr);

    if (customerData.last_name && customerData.last_name.trim()) {
      const lastErr = nameError(customerData.last_name, 'Last name');
      if (lastErr) errs.push(lastErr);
    }

    const phErr = phoneError(customerData.phone);
    if (phErr) errs.push(phErr);

    if (customerData.email && !validateEmail(customerData.email)) {
      errs.push('Enter a valid email address');
    }

    if (customerData.address && customerData.address.trim()) {
      const addrErr = addressError(customerData.address, { label: 'Address' });
      if (addrErr) errs.push(addrErr);
    }

    if (customerData.city && customerData.city.trim()) {
      const cErr = cityError(customerData.city);
      if (cErr) errs.push(cErr);
    }

    if (customerData.state && customerData.state.trim()) {
      const sErr = stateError(customerData.state);
      if (sErr) errs.push(sErr);
    }

    if (customerData.zip && !validateZip(customerData.zip)) {
      errs.push('Enter a valid 5-digit ZIP code');
    }

    if (errs.length > 0) {
      showAlert(errs.join('\n'), { title: 'Fix these to continue' });
      return;
    }

    if (saving) return;
    setSaving(true);

    try {
      // Validate companyId exists
      if (!companyId) {
        console.error('[ExpressJob] No companyId provided');
        showError('Unable to save - company not loaded. Please refresh the page.');
        setSaving(false);
        return;
      }

      const fullName = `${customerData.first_name} ${customerData.last_name || ''}`.trim();
      console.log('[ExpressJob] Creating customer for company:', companyId);

      // Create customer (with offline support)
      const { data: customer, error, queued } = await insertWithSync('customers', {
        company_id: companyId,
        first_name: customerData.first_name,
        last_name: customerData.last_name || '',
        name: fullName,
        phone: customerData.phone,
        email: customerData.email,
        address: customerData.address,
        city: customerData.city,
        state: customerData.state,
        zip: customerData.zip,
        type: 'residential'
      });

      console.log('[ExpressJob] Customer create result:', { customer, error, queued });

      if (error) {
        showError('Error creating customer: ' + error);
        setSaving(false);
        return;
      }

      if (queued) {
        showAlert('Saved offline - will sync when connected');
      }

      setCreatedCustomer(customer);

      // Create lead if source provided (only if online since we need the real customer_id)
      if (customerData.source_partner_id && !queued && customer.id) {
        await insertWithSync('leads', {
          name: fullName,
          phone: customerData.phone,
          email: customerData.email,
          address: customerData.address,
          city: customerData.city,
          state: customerData.state,
          zip: customerData.zip,
          source_partner_id: customerData.source_partner_id,
          status: 'converted',
          customer_id: customer.id
        });
      }

      // If caller pre-selected a workflow type (e.g. "New Visit" vs "New Job"
      // buttons on Today), skip step 2 and jump straight into the flow.
      if (initialWorkflowType) {
        setStep(3);
      } else {
        setStep(2);
      }
    } finally {
      setSaving(false);
    }
  };

  const addItem = (sku) => {
    // Check for duplicates
    const existingItem = quoteItems.find(item => item.sku_id === sku.id);
    if (existingItem) {
      showAlert('This product is already in the quote. Update the quantity instead.', { title: 'Duplicate Product' });
      return;
    }

    // Use the SKU's price directly - no pricing table needed
    const price = parseFloat(sku.price) || parseFloat(sku.base_price) || parseFloat(sku.base_cost) || 0;

    setQuoteItems([...quoteItems, {
      sku_id: sku.id,
      description: sku.name,
      quantity: 1,
      unit_price: price,
      override_price: null // null means use unit_price, otherwise use this
    }]);
  };

  const removeItem = (index) => {
    setQuoteItems(quoteItems.filter((_, i) => i !== index));
  };

  const updateQuantity = (index, quantity) => {
    const updated = [...quoteItems];
    updated[index].quantity = parseInt(quantity) || 1;
    setQuoteItems(updated);
  };

  const updatePrice = (index, price) => {
    const updated = [...quoteItems];
    updated[index].override_price = price === '' ? null : parseFloat(price) || 0;
    setQuoteItems(updated);
  };

  // Manual item entry
  const [manualDescription, setManualDescription] = useState('');
  const [manualPrice, setManualPrice] = useState('');

  const addManualItem = () => {
    if (!manualDescription.trim()) return;
    
    // Check for duplicate manual items (same description)
    const existingItem = quoteItems.find(item => 
      item.sku_id === null && 
      item.description.toLowerCase() === manualDescription.trim().toLowerCase()
    );
    if (existingItem) {
      showAlert('This item is already in the quote. Update the quantity instead.', { title: 'Duplicate Item' });
      return;
    }
    
    const price = parseFloat(manualPrice) || 0;

    setQuoteItems([...quoteItems, {
      sku_id: null, // Manual items don't have a sku
      description: manualDescription.trim(),
      quantity: 1,
      unit_price: price,
      override_price: null
    }]);

    setManualDescription('');
    setManualPrice('');
  };

  const handleQuoteNext = async () => {
    if (quoteItems.length === 0) {
      showAlert('Please add at least one item', { title: 'Items Required' });
      return;
    }
    
    const subtotal = quoteItems.reduce((sum, item) => {
      const price = item.override_price !== null ? item.override_price : item.unit_price;
      return sum + (item.quantity * price);
    }, 0);
    const discountAmount = parseFloat(discount) || 0;
    const afterDiscount = subtotal - discountAmount;
    // Only calculate tax if not disabled
    const taxRate = salesTaxMode === 'disabled' ? 0 : 0.08;
    const tax = afterDiscount * taxRate;
    const total = afterDiscount + tax;

    // Calculate deposit amount
    const depositAmount = depositRequired ? (total * (depositPercentage / 100)) : 0;

    // Create quote (with offline support)
    const { data: quote, error: quoteError, queued: quoteQueued, tempId: quoteTempId } = await insertWithSync('quotes', {
      company_id: companyId,
      customer_id: createdCustomer.id,
      pricing_table_id: selectedPricingTable?.id || null,
      subtotal: subtotal,
      discount: discountAmount,
      tax_rate: taxRate,
      tax_amount: tax,
      total: total,
      notes: notes,
      status: 'draft',
      deposit_required: depositRequired,
      deposit_amount: depositAmount,
      is_recurring: isRecurring,
      recurrence_rule: isRecurring ? recurrenceRule : null,
      recurrence_end_date: isRecurring && recurrenceEndDate ? recurrenceEndDate : null
    });

    if (quoteError) {
      showError('Error creating quote: ' + quoteError);
      return;
    }

    // Create quote items (use override price if set, otherwise unit_price)
    const quoteItemsData = quoteItems.map(item => {
      const finalPrice = item.override_price !== null ? item.override_price : item.unit_price;
      return {
        quote_id: quote.id, // Will be temp_id if offline
        sku_id: item.sku_id,
        quantity: item.quantity,
        unit_price: finalPrice,
        total_price: item.quantity * finalPrice,
        description: item.description
      };
    });

    // Insert quote items (offline queue will handle dependency)
    if (quoteQueued) {
      // Queue the items with dependency on the quote
      for (const item of quoteItemsData) {
        await insertWithSync('quote_items', item, { dependsOn: quoteTempId });
      }
    } else {
      // Online - direct insert
      const { error: itemsError } = await supabaseData
        .from('quote_items')
        .insert(quoteItemsData);

      if (itemsError) {
        showError('Error creating quote items: ' + itemsError.message);
        return;
      }
    }
    
    // If offline, skip to scheduling (signing requires internet)
    if (quoteQueued) {
      showAlert('Quote saved offline. Signing requires internet - skipping to scheduling.', { title: 'Offline Mode' });
      setCreatedQuote({ ...quote, customers: createdCustomer });
      setStep(4); // Skip to scheduling
      return;
    }

    // Fetch the complete quote with items for signing
    const { data: completeQuote, error: fetchError } = await supabaseData
      .from('quotes')
      .select(`
        *,
        customers (
          id,
          name,
          email,
          phone,
          address,
          city,
          state,
          zip
        )
      `)
      .eq('id', quote.id)
      .single();

    if (fetchError) {
      showError('Error fetching quote: ' + fetchError.message);
      return;
    }

    setCreatedQuote(completeQuote);
    setShowSignModal(true);
  };

  const handleSigned = async (pdfUrl) => {
    // Quote is signed, now move to scheduling step
    setShowSignModal(false);
    setStep(4); // Step 4 for full workflow (schedule)
  };

  // Save quote as draft and close workflow
  const handleSaveAsDraft = async () => {
    if (quoteItems.length === 0) {
      showAlert('Please add at least one item', { title: 'Items Required' });
      return;
    }

    // Check for duplicate quote (same customer, similar items, within last 5 minutes)
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
    const { data: recentQuotes, error: recentError } = await supabaseData
      .from('quotes')
      .select('id, total, created_at, quote_items(sku_id, quantity)')
      .eq('customer_id', createdCustomer.id)
      .eq('company_id', companyId)
      .gte('created_at', fiveMinutesAgo)
      .order('created_at', { ascending: false })
      .limit(5);

    if (!recentError && recentQuotes && recentQuotes.length > 0) {
      // Check for potential duplicate (similar total within $1, same item count)
      const currentItemCount = quoteItems.length;
      const currentTotal = quoteItems.reduce((sum, item) => {
        const price = item.override_price !== null ? item.override_price : item.unit_price;
        return sum + (item.quantity * price);
      }, 0);

      const duplicate = recentQuotes.find(q => {
        const totalMatch = Math.abs(q.total - currentTotal) < 1;
        const itemCountMatch = q.quote_items?.length === currentItemCount;
        return totalMatch && itemCountMatch;
      });

      if (duplicate) {
        const confirmResult = await showConfirm(
          `A similar quote was created just now (${new Date(duplicate.created_at).toLocaleTimeString()}). Create another?`,
          { 
            title: 'Possible Duplicate Quote',
            confirmText: 'Create Anyway',
            cancelText: 'Cancel'
          }
        );
        if (!confirmResult) return;
      }
    }

    const subtotal = quoteItems.reduce((sum, item) => {
      const price = item.override_price !== null ? item.override_price : item.unit_price;
      return sum + (item.quantity * price);
    }, 0);
    const discountAmount = parseFloat(discount) || 0;
    const afterDiscount = subtotal - discountAmount;
    const taxRate = salesTaxMode === 'disabled' ? 0 : 0.08;
    const tax = afterDiscount * taxRate;
    const total = afterDiscount + tax;
    const depositAmount = depositRequired ? (total * (depositPercentage / 100)) : 0;

    const { data: quote, error: quoteError, queued: quoteQueued, tempId: quoteTempId } = await insertWithSync('quotes', {
      company_id: companyId,
      customer_id: createdCustomer.id,
      pricing_table_id: selectedPricingTable?.id || null,
      subtotal: subtotal,
      discount: discountAmount,
      tax_rate: taxRate,
      tax_amount: tax,
      total: total,
      notes: notes,
      status: 'draft',
      deposit_required: depositRequired,
      deposit_amount: depositAmount,
      is_recurring: isRecurring,
      recurrence_rule: isRecurring ? recurrenceRule : null,
      recurrence_end_date: isRecurring && recurrenceEndDate ? recurrenceEndDate : null
    });

    if (quoteError) {
      showError('Error creating quote: ' + quoteError);
      return;
    }

    const quoteItemsData = quoteItems.map(item => {
      const finalPrice = item.override_price !== null ? item.override_price : item.unit_price;
      return {
        quote_id: quote.id,
        sku_id: item.sku_id,
        quantity: item.quantity,
        unit_price: finalPrice,
        total_price: item.quantity * finalPrice,
        description: item.description
      };
    });

    if (quoteQueued) {
      for (const item of quoteItemsData) {
        await insertWithSync('quote_items', item, { dependsOn: quoteTempId });
      }
    } else {
      const { error: itemsError } = await supabaseData
        .from('quote_items')
        .insert(quoteItemsData);

      if (itemsError) {
        showError('Error creating quote items: ' + itemsError.message);
        return;
      }
    }

    showSuccess('Quote saved as draft!');
    onClose();
  };

  // Save quote and open send modal
  const handleSaveAndSend = async () => {
    if (quoteItems.length === 0) {
      showAlert('Please add at least one item', { title: 'Items Required' });
      return;
    }

    // Check for duplicate quote (same customer, similar items, within last 5 minutes)
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
    const { data: recentQuotes, error: recentError } = await supabaseData
      .from('quotes')
      .select('id, total, created_at, quote_items(sku_id, quantity)')
      .eq('customer_id', createdCustomer.id)
      .eq('company_id', companyId)
      .gte('created_at', fiveMinutesAgo)
      .order('created_at', { ascending: false })
      .limit(5);

    if (!recentError && recentQuotes && recentQuotes.length > 0) {
      // Check for potential duplicate (similar total within $1, same item count)
      const currentItemCount = quoteItems.length;
      const currentTotal = quoteItems.reduce((sum, item) => {
        const price = item.override_price !== null ? item.override_price : item.unit_price;
        return sum + (item.quantity * price);
      }, 0);

      const duplicate = recentQuotes.find(q => {
        const totalMatch = Math.abs(q.total - currentTotal) < 1;
        const itemCountMatch = q.quote_items?.length === currentItemCount;
        return totalMatch && itemCountMatch;
      });

      if (duplicate) {
        const confirmResult = await showConfirm(
          `A similar quote was created just now (${new Date(duplicate.created_at).toLocaleTimeString()}). Create another?`,
          { 
            title: 'Possible Duplicate Quote',
            confirmText: 'Create Anyway',
            cancelText: 'Cancel'
          }
        );
        if (!confirmResult) return;
      }
    }

    const subtotal = quoteItems.reduce((sum, item) => {
      const price = item.override_price !== null ? item.override_price : item.unit_price;
      return sum + (item.quantity * price);
    }, 0);
    const discountAmount = parseFloat(discount) || 0;
    const afterDiscount = subtotal - discountAmount;
    const taxRate = salesTaxMode === 'disabled' ? 0 : 0.08;
    const tax = afterDiscount * taxRate;
    const total = afterDiscount + tax;
    const depositAmount = depositRequired ? (total * (depositPercentage / 100)) : 0;

    const { data: quote, error: quoteError } = await insertWithSync('quotes', {
      company_id: companyId,
      customer_id: createdCustomer.id,
      pricing_table_id: selectedPricingTable?.id || null,
      subtotal: subtotal,
      discount: discountAmount,
      tax_rate: taxRate,
      tax_amount: tax,
      total: total,
      notes: notes,
      status: 'draft',
      deposit_required: depositRequired,
      deposit_amount: depositAmount,
      is_recurring: isRecurring,
      recurrence_rule: isRecurring ? recurrenceRule : null,
      recurrence_end_date: isRecurring && recurrenceEndDate ? recurrenceEndDate : null
    });

    if (quoteError) {
      showError('Error creating quote: ' + quoteError);
      return;
    }

    const quoteItemsData = quoteItems.map(item => {
      const finalPrice = item.override_price !== null ? item.override_price : item.unit_price;
      return {
        quote_id: quote.id,
        sku_id: item.sku_id,
        quantity: item.quantity,
        unit_price: finalPrice,
        total_price: item.quantity * finalPrice,
        description: item.description
      };
    });

    const { error: itemsError } = await supabaseData
      .from('quote_items')
      .insert(quoteItemsData);

    if (itemsError) {
      showError('Error creating quote items: ' + itemsError.message);
      return;
    }

    // Fetch complete quote for send modal
    const { data: completeQuote } = await supabaseData
      .from('quotes')
      .select(`*, customers (id, name, email, phone)`)
      .eq('id', quote.id)
      .single();

    setCreatedQuote(completeQuote);
    // Initialize send modal fields from customer
    setSendModalEmail(createdCustomer?.email || '');
    setSendModalPhone(createdCustomer?.phone || '');
    // Auto-select best send method based on available contact info
    const hasEmail = !!createdCustomer?.email?.trim();
    const hasPhone = !!createdCustomer?.phone?.trim();
    if (hasEmail && hasPhone) setSendMethod('both');
    else if (hasPhone) setSendMethod('sms');
    else setSendMethod('email');
    setEditingContactInfo(false);
    setAdditionalRecipients([]);
    setShowSendModal(true);
  };

  // Get the quote URL for remote signing
  const getQuoteUrl = (quoteId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/quote/${quoteId}`;
  };

  // Copy quote link to clipboard
  const copyQuoteLink = async () => {
    if (!createdQuote?.id) return;
    const url = getQuoteUrl(createdQuote.id);
    try {
      await navigator.clipboard.writeText(url);
      setQuoteLinkCopied(true);
      setTimeout(() => setQuoteLinkCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setQuoteLinkCopied(true);
      setTimeout(() => setQuoteLinkCopied(false), 2000);
    }
  };

  // Send quote via email/SMS
  const handleSendQuote = async () => {
    if (!createdQuote?.id) return;

    const customer = createdQuote.customers || createdCustomer;
    if (!customer) {
      showError('Customer not found');
      return;
    }

    // Use the editable modal fields
    const primaryEmail = sendModalEmail?.trim();
    const primaryPhone = sendModalPhone?.trim();

    const quoteUrl = getQuoteUrl(createdQuote.id);

    // Build list of all recipients (primary + additional)
    const allEmails = [primaryEmail, ...additionalRecipients.map(r => r.email?.trim())].filter(Boolean);
    const allPhones = [primaryPhone, ...additionalRecipients.map(r => r.phone?.trim())].filter(Boolean);

    setSendingQuote(true);
    try {
      let emailsSent = 0;
      let smsSent = 0;

      // Update customer record if contact info was changed
      if (primaryEmail !== customer.email || primaryPhone !== customer.phone) {
        const updateData = {};
        if (primaryEmail && primaryEmail !== customer.email) updateData.email = primaryEmail;
        if (primaryPhone && primaryPhone !== customer.phone) updateData.phone = primaryPhone;
        if (Object.keys(updateData).length > 0) {
          await supabaseData
            .from('customers')
            .update(updateData)
            .eq('id', customer.id);
        }
      }

      // Send emails if selected
      if (sendMethod === 'email' || sendMethod === 'both') {
        for (const email of allEmails) {
          const emailResponse = await fetch('/api/email-send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: email,
              subject: `You have a quote ready for review`,
              html: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                  <h2 style="color: #1a1a1a;">You have a new quote ready for review</h2>
                  <p style="color: #666;">Hi ${customer.name || 'there'},</p>
                  <p style="color: #666;">Please review and sign your quote by clicking the button below:</p>
                  <div style="text-align: center; margin: 2rem 0;">
                    <a href="${quoteUrl}" style="background: #F05A28; color: white; padding: 1rem 2rem; text-decoration: none; border-radius: 8px; font-weight: bold; display: inline-block;">
                      View & Sign Quote
                    </a>
                  </div>
                  <p style="color: #999; font-size: 0.9rem;">Or copy this link: ${quoteUrl}</p>
                </div>
              `,
              companyId: companyId
            })
          });
          if (emailResponse.ok) emailsSent++;
        }
      }

      // Send SMS if selected
      if (sendMethod === 'sms' || sendMethod === 'both') {
        if (textingEnabled) {
          // Send via API (Telnyx/Twilio)
          for (const phone of allPhones) {
            const smsResponse = await fetch('/api/sms-send', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                to: phone,
                message: `You have a new quote ready for review. View and sign here: ${quoteUrl}`,
                customerId: customer.id,
                companyId: companyId
              })
            });
            if (smsResponse.ok) smsSent++;
          }
        } else {
          // No texting subscription — open native SMS app
          const smsBody = encodeURIComponent(`You have a new quote ready for review. View and sign here: ${quoteUrl}`);
          const cleanPhone = (allPhones[0] || '').replace(/\D/g, '');
          window.location.href = `sms:${cleanPhone}?body=${smsBody}`;
          smsSent = 1; // Count as sent since user will send from their phone
        }
      }

      // Update quote status to 'sent'
      await supabaseData
        .from('quotes')
        .update({ status: 'sent', sent_at: new Date().toISOString() })
        .eq('id', createdQuote.id);

      if (emailsSent > 0 || smsSent > 0) {
        const methods = [];
        if (emailsSent > 0) methods.push(`${emailsSent} email${emailsSent > 1 ? 's' : ''}`);
        if (smsSent > 0) methods.push(textingEnabled ? `${smsSent} SMS` : 'SMS (from your phone)');
        showSuccess(`Quote sent via ${methods.join(' and ')}!`);
        setShowSendModal(false);
        onClose();
      } else if (!primaryEmail && !primaryPhone) {
        showError('Please add an email or phone number to send the quote.');
      } else {
        showError('Failed to send quote. Please check the contact info and try again.');
      }
    } catch (err) {
      showError('Failed to send quote');
    } finally {
      setSendingQuote(false);
    }
  };

  const createJob = async (schedDate, schedTime) => {
    if (creatingJob) return;
    setCreatingJob(true);
    // Generate job number
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const time = String(date.getTime()).slice(-4);
    const jobNumber = `JOB-${year}${month}${day}-${time}`;

    // Build job data based on workflow type
    const jobData = {
      company_id: companyId,
      job_number: jobNumber,
      customer_id: createdCustomer.id,
      job_type: workflowType === 'quick' ? 'quote_visit' : 'repair',
      status: 'scheduled',
      priority: 'normal',
      scheduled_date: schedDate,
      scheduled_time: schedTime,
      estimated_duration: estimatedDuration,
      assigned_tech: selectedTech || null
    };

    // Add quote fee for quote_visit jobs
    if (workflowType === 'quick' && quoteFeeEnabled) {
      jobData.quote_fee_amount = quoteFeeAmount;
      jobData.quote_fee_status = 'pending';
    }

    // Add recurring job data if enabled
    if (isRecurring && recurrenceEndDate) {
      jobData.is_recurring = true;
      jobData.recurrence_rule = recurrenceRule;
      jobData.recurrence_end_date = recurrenceEndDate;
    }

    // Add auto-charge flag if enabled
    if (autoChargeEnabled && createdCustomer?.default_payment_method_id) {
      jobData.auto_charge_enabled = true;
    }

    // Add quote-related data only for full workflow
    if (workflowType === 'full' && createdQuote) {
      jobData.quoted_value = createdQuote.total;
      jobData.pricing_table_id = selectedPricingTable?.id;
    }

    // Create job (with offline support)
    const { data: job, error, queued } = await insertWithSync('jobs', jobData);

    if (error) {
      setCreatingJob(false);
      showError('Error creating job: ' + error);
      return;
    }

    // Generate recurring instances if this is a recurring job (only if online)
    let recurringCount = 0;
    if (isRecurring && recurrenceEndDate && job && !queued) {
      try {
        const result = await generateRecurringInstances(job);
        if (result.success && result.instances) {
          recurringCount = result.instances.length;
        }
      } catch (recurringError) {
        // Don't fail the whole operation, just warn
      }
    }

    // Send notification to assigned tech (if not self-assigned)
    if (job && selectedTech && selectedTech !== currentUserId && !queued) {
      try {
        const techName = technicians.find(t => t.id === selectedTech)?.name || '';
        const custName = createdCustomer?.first_name || createdCustomer?.name || 'Customer';
        await supabaseData.from('notifications').insert({
          company_id: companyId,
          user_id: selectedTech,
          type: 'job_assigned',
          title: 'New Job Assigned',
          message: `${custName} - ${schedDate} at ${schedTime}`,
          data: { job_id: job.id, customer_id: createdCustomer?.id },
          read: false
        });
      } catch (notifErr) {
        // Don't fail job creation if notification fails
      }
    }

    const instanceCount = recurringCount > 0 ? ` (+ ${recurringCount} recurring instances)` : '';
    const offlineNote = queued ? ' (saved offline)' : '';
    showSuccess((workflowType === 'quick' ? 'Visit scheduled!' : 'Job created successfully!') + instanceCount + offlineNote);
    onComplete(job);
  };

  const handleDoNow = () => {
    // Require tech selection for office/admin
    if (needsTechSelection && !selectedTech) {
      showAlert('Please select a technician to assign this job to', { title: 'Technician Required' });
      return;
    }
    const now = new Date();
    const todayDate = getTodayString(); // Use local timezone, not UTC
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    createJob(todayDate, currentTime);
  };

  const handleScheduleLater = () => {
    // Require tech selection for office/admin
    if (needsTechSelection && !selectedTech) {
      showAlert('Please select a technician to assign this job to', { title: 'Technician Required' });
      return;
    }
    if (!scheduledDate) {
      showAlert('Please select a date', { title: 'Date Required' });
      return;
    }
    createJob(scheduledDate, scheduledTime);
  };

  const subtotal = quoteItems.reduce((sum, item) => {
    const price = item.override_price !== null ? item.override_price : item.unit_price;
    return sum + (item.quantity * price);
  }, 0);
  const discountAmount = parseFloat(discount) || 0;
  const afterDiscount = subtotal - discountAmount;
  // Only calculate tax if not disabled
  const displayTaxRate = salesTaxMode === 'disabled' ? 0 : 0.08;
  const tax = afterDiscount * displayTaxRate;
  const total = afterDiscount + tax;

  if (showSignModal && createdQuote && createdCustomer) {
    // Add total_price to each item for the signature modal
    const itemsWithTotal = quoteItems.map(item => ({
      ...item,
      total_price: item.quantity * item.unit_price
    }));
    
    return (
      <QuoteSignatureModal
        quote={createdQuote}
        customer={createdQuote.customers || createdCustomer}
        quoteItems={itemsWithTotal}
        onClose={() => setShowSignModal(false)}
        onBack={() => setShowSignModal(false)}
        onSigned={handleSigned}
      />
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '1rem',
      overflow: 'auto'
    }}>
      <div style={{
        background: colors.bgInput,
        borderRadius: '12px',
        width: '100%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: `2px solid ${colors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          background: colors.bgInput,
          zIndex: 10
        }}>
          <div>
            <h2 style={{ fontSize: '1.5rem', margin: '0 0 0.5rem 0', color: colors.accent }}>
              📋 {workflowType === 'quick' ? 'New Visit' : workflowType === 'full' ? 'New Job' : 'New Appointment'}
            </h2>
            <div style={{ fontSize: '0.9rem', color: colors.textMuted }}>
              {!workflowType ? (
                step === 1 ? 'Step 1: Customer Info' : 'Step 2: Choose Type'
              ) : workflowType === 'quick' ? (
                `Step ${step === 1 ? 1 : step === 2 ? 2 : 3} of 3: ${
                  step === 1 ? 'Customer Info' :
                  step === 2 ? 'Choose Type' :
                  'Schedule'
                }`
              ) : (
                `Step ${step} of 4: ${
                  step === 1 ? 'Customer Info' :
                  step === 2 ? 'Choose Type' :
                  step === 3 ? 'Build Quote & Sign' :
                  'Schedule'
                }`
              )}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none',
            border: 'none',
            color: colors.textMuted,
            cursor: 'pointer',
            padding: '0.5rem'
          }}>
            <X size={24} />
          </button>
        </div>

        {/* Step Progress */}
        <div style={{
          display: 'flex',
          padding: '1rem 1.5rem',
          gap: '1rem',
          background: colors.bgCard
        }}>
          {/* Customer Info - always shown */}
          <div style={{
            flex: 1,
            height: '4px',
            background: step >= 1 ? colors.accent : colors.border,
            borderRadius: '2px'
          }} />
          {/* Choose Type - always shown */}
          <div style={{
            flex: 1,
            height: '4px',
            background: step >= 2 ? colors.accent : colors.border,
            borderRadius: '2px'
          }} />
          {/* Quote/Sign - only for full workflow or before choosing */}
          {(!workflowType || workflowType === 'full') && (
            <div style={{
              flex: 1,
              height: '4px',
              background: step >= 3 ? colors.accent : colors.border,
              borderRadius: '2px'
            }} />
          )}
          {/* Schedule - always shown */}
          <div style={{
            flex: 1,
            height: '4px',
            background: (workflowType === 'quick' ? step >= 3 : step >= 4) ? colors.accent : colors.border,
            borderRadius: '2px'
          }} />
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>
          {/* STEP 1: Customer Info */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={customerData.first_name}
                    onChange={(e) => setCustomerData({ ...customerData, first_name: e.target.value })}
                    placeholder="John"
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                    autoFocus
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={customerData.last_name}
                    onChange={(e) => setCustomerData({ ...customerData, last_name: e.target.value })}
                    placeholder="Doe"
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                    Phone *
                  </label>
                  <input
                    type="tel"
                    value={customerData.phone}
                    onChange={(e) => setCustomerData({ ...customerData, phone: e.target.value })}
                    placeholder="555-1234"
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={customerData.email}
                    onChange={(e) => setCustomerData({ ...customerData, email: e.target.value })}
                    placeholder="john@email.com"
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                  Address
                </label>
                <input
                  type="text"
                  value={customerData.address}
                  onChange={(e) => setCustomerData({ ...customerData, address: e.target.value })}
                  placeholder="123 Main St"
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                    City
                  </label>
                  <input
                    type="text"
                    value={customerData.city}
                    onChange={(e) => setCustomerData({ ...customerData, city: e.target.value })}
                    placeholder="Phoenix"
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                    State
                  </label>
                  <input
                    type="text"
                    value={customerData.state}
                    onChange={(e) => setCustomerData({ ...customerData, state: e.target.value })}
                    placeholder="AZ"
                    maxLength="2"
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem',
                      textTransform: 'uppercase'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                    ZIP
                  </label>
                  <input
                    type="text"
                    value={customerData.zip}
                    onChange={(e) => setCustomerData({ ...customerData, zip: e.target.value })}
                    placeholder="85001"
                    maxLength="5"
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>

              {/* Lead Source - Only show for Team/Unlimited plans with commissions feature */}
              {hasFeature('commissions') && partners.length > 0 && (
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                    Lead Source (Optional)
                  </label>
                  <select
                    value={customerData.source_partner_id}
                    onChange={(e) => setCustomerData({ ...customerData, source_partner_id: e.target.value })}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '0.875rem 2.5rem 0.875rem 0.875rem',
                      background: `${colors.bgCard} url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>") right 0.875rem center / 14px no-repeat`,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem',
                      cursor: 'pointer',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none'
                    }}
                  >
                    <option value="">Please select</option>
                    {partners.map(p => (
                      <option key={p.id} value={p.id}>{p.partner_name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  onClick={onClose}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: 'transparent',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.textMuted,
                    fontSize: '1rem',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCustomerNext}
                  disabled={saving}
                  style={{
                    flex: 2,
                    padding: '1rem',
                    background: saving ? colors.border : colors.accent,
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '1.1rem',
                    fontWeight: '700',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    opacity: saving ? 0.7 : 1
                  }}
                >
                  {saving ? 'Saving...' : 'Next'} {!saving && <ArrowRight size={20} />}
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: Choose Workflow Type */}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div style={{
                padding: '1rem',
                background: colors.bgCard,
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '0.9rem', color: colors.textMuted, marginBottom: '0.5rem' }}>Customer:</div>
                <div style={{ fontSize: '1.2rem', fontWeight: '600', color: colors.text }}>
                  {createdCustomer?.name || `${createdCustomer?.first_name || ''} ${createdCustomer?.last_name || ''}`.trim()}
                </div>
                <div style={{ fontSize: '0.9rem', color: colors.textMuted }}>{createdCustomer?.phone}</div>
                {createdCustomer?.address && (
                  <div style={{ fontSize: '0.9rem', color: colors.textMuted }}>{createdCustomer?.address}</div>
                )}
              </div>

              <div style={{ textAlign: 'center' }}>
                <h3 style={{ color: colors.text, marginBottom: '0.5rem' }}>What would you like to do?</h3>
                <p style={{ color: colors.textMuted, fontSize: '0.9rem' }}>Choose based on whether you know the pricing now</p>
              </div>

              <div style={{ display: 'grid', gap: '1rem' }}>
                {/* Quick Visit Option */}
                <button
                  onClick={() => {
                    setWorkflowType('quick');
                    setStep(3); // Go to schedule
                  }}
                  style={{
                    padding: '1.5rem',
                    background: colors.bgCard,
                    border: `2px solid ${colors.info}`,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      background: `${colors.info}20`,
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Calendar size={28} color={colors.info} />
                    </div>
                    <div>
                      <div style={{ fontSize: '1.2rem', fontWeight: '700', color: colors.text, marginBottom: '0.25rem' }}>
                        Schedule a Visit
                      </div>
                      <div style={{ fontSize: '0.9rem', color: colors.textMuted }}>
                        Just book the appointment - quote on site
                      </div>
                    </div>
                  </div>
                </button>

                {/* Full Quote Option */}
                <button
                  onClick={() => {
                    setWorkflowType('full');
                    setStep(3); // Go to quote builder
                  }}
                  style={{
                    padding: '1.5rem',
                    background: colors.bgCard,
                    border: `2px solid ${colors.accent}`,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      background: `${colors.accent}20`,
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <FileText size={28} color={colors.accent} />
                    </div>
                    <div>
                      <div style={{ fontSize: '1.2rem', fontWeight: '700', color: colors.text, marginBottom: '0.25rem' }}>
                        Build a Quote
                      </div>
                      <div style={{ fontSize: '0.9rem', color: colors.textMuted }}>
                        Add items, get signature, then schedule
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  onClick={() => setStep(1)}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: 'transparent',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.textMuted,
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <ArrowLeft size={16} /> Back
                </button>
                <button
                  onClick={onClose}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: 'transparent',
                    border: `1px solid ${colors.error || '#ef4444'}`,
                    borderRadius: '8px',
                    color: colors.error || '#ef4444',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Quote Builder (Full workflow only) */}
          {step === 3 && workflowType === 'full' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '0.75rem' : '1rem' }}>
              <div style={{
                padding: isMobile ? '0.75rem' : '1rem',
                background: colors.bgCard,
                borderRadius: '8px',
                marginBottom: isMobile ? '0.5rem' : '1rem'
              }}>
                <div style={{ fontSize: isMobile ? '0.8rem' : '0.9rem', color: colors.textMuted, marginBottom: '0.25rem' }}>Customer:</div>
                <div style={{ fontSize: isMobile ? '1rem' : '1.2rem', fontWeight: '600', color: colors.text }}>
                  {createdCustomer?.name || `${createdCustomer?.first_name || ''} ${createdCustomer?.last_name || ''}`.trim()}
                </div>
                <div style={{ fontSize: isMobile ? '0.8rem' : '0.9rem', color: colors.textMuted }}>{createdCustomer?.phone}</div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600', fontSize: isMobile ? '0.9rem' : '1rem' }}>
                  Add Items
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {/* Add from Catalog - dropdown */}
                  <div ref={productDropdownRef} style={{ position: 'relative' }}>
                    <button
                      onClick={() => {
                        setShowProductDropdown(!showProductDropdown);
                        setProductSearch('');
                      }}
                      disabled={skus.length === 0}
                      style={{
                        background: skus.length === 0 ? colors.bgInput : colors.success,
                        color: skus.length === 0 ? colors.textMuted : '#000',
                        border: 'none',
                        padding: isMobile ? '0.6rem 1rem' : '0.75rem 1.25rem',
                        borderRadius: '8px',
                        fontSize: isMobile ? '0.85rem' : '0.95rem',
                        fontWeight: 'bold',
                        cursor: skus.length === 0 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <Package size={18} />
                      {skus.length === 0 ? 'No Products' : 'Add Product'}
                      {skus.length > 0 && <ChevronDown size={16} style={{ transform: showProductDropdown ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />}
                    </button>

                    {/* Dropdown */}
                    {showProductDropdown && skus.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '0.5rem',
                        width: isMobile ? '280px' : '320px',
                        maxHeight: '350px',
                        background: colors.bgCard,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                        zIndex: 100,
                        overflow: 'hidden'
                      }}>
                        <div style={{ padding: '0.75rem', borderBottom: `1px solid ${colors.border}` }}>
                          <input
                            type="text"
                            placeholder="Search products..."
                            value={productSearch}
                            onChange={(e) => setProductSearch(e.target.value)}
                            autoFocus
                            style={{
                              width: '100%',
                              padding: '0.5rem 0.75rem',
                              background: colors.bgInput,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              color: colors.text,
                              fontSize: '0.9rem',
                              outline: 'none'
                            }}
                          />
                        </div>
                        <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                          {skus
                            .filter(sku => {
                              const matchesSearch = sku.name.toLowerCase().includes(productSearch.toLowerCase()) ||
                                sku.code?.toLowerCase().includes(productSearch.toLowerCase());
                              return matchesSearch;
                            })
                            .map(sku => (
                              <div
                                key={sku.id}
                                onClick={() => {
                                  addItem(sku);
                                  setShowProductDropdown(false);
                                }}
                                style={{
                                  padding: '0.75rem 1rem',
                                  cursor: 'pointer',
                                  borderBottom: `1px solid ${colors.border}`,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  transition: 'background 0.15s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.background = colors.bgInput}
                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                              >
                                <div>
                                  <div style={{ fontWeight: '500', color: colors.text, fontSize: '0.9rem' }}>
                                    {sku.name}
                                  </div>
                                  {sku.code && (
                                    <div style={{ fontSize: '0.75rem', color: colors.textMuted }}>
                                      {sku.code}
                                    </div>
                                  )}
                                </div>
                                <div style={{ color: colors.success, fontWeight: 'bold', fontSize: '0.9rem' }}>
                                  ${parseFloat(sku.price || sku.base_price || sku.base_cost || 0).toFixed(2)}
                                </div>
                              </div>
                            ))}
                          {skus.filter(sku => {
                            const matchesSearch = sku.name.toLowerCase().includes(productSearch.toLowerCase()) ||
                              sku.code?.toLowerCase().includes(productSearch.toLowerCase());
                            return matchesSearch;
                          }).length === 0 && (
                            <div style={{ padding: '1rem', textAlign: 'center', color: colors.textMuted }}>
                              No products found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Manual Entry Section */}
              <div style={{
                padding: isMobile ? '0.75rem' : '1rem',
                background: colors.bgCard,
                borderRadius: '8px',
                border: `2px dashed ${colors.border}`
              }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600', fontSize: isMobile ? '0.85rem' : '1rem' }}>
                  Add Custom Item
                </label>
                <div style={{ display: 'flex', gap: isMobile ? '0.4rem' : '0.5rem', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder={isMobile ? "Description" : "Description (e.g., Spring Repair)"}
                    value={manualDescription}
                    onChange={(e) => setManualDescription(e.target.value)}
                    style={{
                      flex: isMobile ? '1 1 100%' : 2,
                      minWidth: isMobile ? 'auto' : '150px',
                      padding: isMobile ? '0.6rem' : '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      fontSize: isMobile ? '0.85rem' : '0.9rem'
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Price"
                    value={manualPrice}
                    onChange={(e) => setManualPrice(e.target.value)}
                    min="0"
                    step="0.01"
                    style={{
                      width: isMobile ? '80px' : '100px',
                      flex: isMobile ? 1 : 'none',
                      padding: isMobile ? '0.6rem' : '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      fontSize: isMobile ? '0.85rem' : '0.9rem',
                      textAlign: 'right'
                    }}
                  />
                  <button
                    onClick={addManualItem}
                    disabled={!manualDescription.trim()}
                    style={{
                      padding: isMobile ? '0.6rem 1rem' : '0.75rem 1.25rem',
                      background: manualDescription.trim() ? colors.success : colors.border,
                      border: 'none',
                      borderRadius: '6px',
                      color: manualDescription.trim() ? '#fff' : '#666',
                      fontSize: isMobile ? '0.85rem' : '0.9rem',
                      fontWeight: '600',
                      cursor: manualDescription.trim() ? 'pointer' : 'not-allowed',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    + Add
                  </button>
                </div>
                <div style={{ fontSize: isMobile ? '0.75rem' : '0.8rem', color: colors.textMuted, marginTop: '0.5rem' }}>
                  Use this to add items not in your product list
                </div>
              </div>

              {quoteItems.length > 0 && (
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600', fontSize: isMobile ? '0.9rem' : '1rem' }}>
                    Quote Items
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '0.4rem' : '0.5rem' }}>
                    {quoteItems.map((item, index) => {
                      const displayPrice = item.override_price !== null ? item.override_price : item.unit_price;
                      const lineTotal = item.quantity * displayPrice;

                      return (
                        <div key={index} style={{
                          padding: isMobile ? '0.75rem' : '1rem',
                          background: colors.bgCard,
                          borderRadius: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: isMobile ? '0.5rem' : '0.75rem'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.5rem' : '1rem' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', color: colors.text, fontSize: isMobile ? '0.9rem' : '1rem' }}>{item.description}</div>
                              <div style={{ fontSize: isMobile ? '0.75rem' : '0.85rem', color: colors.textMuted }}>
                                {item.sku_id ? (
                                  <>
                                    Table: ${item.unit_price.toFixed(2)}
                                    {item.override_price !== null && (
                                      <span style={{ color: colors.accent, marginLeft: '0.25rem' }}>
                                        (Override)
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <span style={{ color: colors.info }}>Custom item</span>
                                )}
                              </div>
                            </div>
                            <div style={{ fontWeight: '700', color: colors.success, fontSize: isMobile ? '1rem' : '1.1rem' }}>
                              ${lineTotal.toFixed(2)}
                            </div>
                            <button
                              onClick={() => removeItem(index)}
                              style={{
                                padding: isMobile ? '0.4rem' : '0.5rem',
                                background: colors.border,
                                border: 'none',
                                borderRadius: '6px',
                                color: colors.error,
                                cursor: 'pointer'
                              }}
                            >
                              <X size={isMobile ? 14 : 16} />
                            </button>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.5rem' : '1rem', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                              <label style={{ fontSize: isMobile ? '0.7rem' : '0.75rem', color: colors.textMuted }}>Qty</label>
                              <input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateQuantity(index, e.target.value)}
                                style={{
                                  width: isMobile ? '50px' : '70px',
                                  padding: isMobile ? '0.4rem' : '0.5rem',
                                  background: colors.bgInput,
                                  border: `1px solid ${colors.border}`,
                                  borderRadius: '6px',
                                  color: colors.text,
                                  textAlign: 'center',
                                  fontSize: isMobile ? '0.85rem' : '1rem'
                                }}
                              />
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1 }}>
                              <label style={{ fontSize: isMobile ? '0.7rem' : '0.75rem', color: colors.textMuted }}>
                                {isMobile ? 'Override $' : 'Override Price (leave blank to use table price)'}
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder={`$${item.unit_price.toFixed(2)}`}
                                value={item.override_price !== null ? item.override_price : ''}
                                onChange={(e) => updatePrice(index, e.target.value)}
                                style={{
                                  padding: isMobile ? '0.4rem' : '0.5rem',
                                  background: colors.bgInput,
                                  border: item.override_price !== null ? `1px solid ${colors.accent}` : `1px solid ${colors.border}`,
                                  borderRadius: '6px',
                                  color: colors.text,
                                  fontSize: isMobile ? '0.85rem' : '1rem'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    background: colors.bgCard,
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ color: colors.textMuted }}>Subtotal:</span>
                      <span style={{ color: colors.text, fontWeight: '600' }}>${subtotal.toFixed(2)}</span>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '0.5rem',
                      gap: '1rem'
                    }}>
                      <span style={{ color: colors.textMuted }}>Discount:</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={discount || ''}
                        onChange={(e) => setDiscount(e.target.value)}
                        style={{
                          width: '120px',
                          padding: '0.5rem',
                          background: colors.bgInput,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '6px',
                          color: colors.text,
                          textAlign: 'right'
                        }}
                      />
                    </div>

                    {discountAmount > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span style={{ color: colors.textMuted }}>After Discount:</span>
                        <span style={{ color: colors.text, fontWeight: '600' }}>${afterDiscount.toFixed(2)}</span>
                      </div>
                    )}

                    {salesTaxMode !== 'disabled' && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span style={{ color: colors.textMuted }}>Tax ({(displayTaxRate * 100).toFixed(0)}%):</span>
                        <span style={{ color: colors.text, fontWeight: '600' }}>${tax.toFixed(2)}</span>
                      </div>
                    )}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      paddingTop: '0.5rem',
                      borderTop: `1px solid ${colors.border}`
                    }}>
                      <span style={{ color: colors.text, fontSize: '1.2rem', fontWeight: '700' }}>Total:</span>
                      <span style={{ color: colors.success, fontSize: '1.2rem', fontWeight: '700' }}>${total.toFixed(2)}</span>
                    </div>

                    {/* Deposit Toggle */}
                    <div style={{
                      marginTop: '1rem',
                      paddingTop: '1rem',
                      borderTop: `1px solid ${colors.border}`
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: depositRequired ? '1rem' : 0
                      }}>
                        <span style={{ color: colors.text, fontWeight: '600' }}>Require Deposit</span>
                        <div
                          onClick={() => setDepositRequired(!depositRequired)}
                          style={{
                            width: '50px',
                            height: '26px',
                            background: depositRequired ? colors.success : colors.border,
                            borderRadius: '13px',
                            cursor: 'pointer',
                            position: 'relative',
                            transition: 'background 0.2s'
                          }}
                        >
                          <div style={{
                            width: '22px',
                            height: '22px',
                            background: '#fff',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '2px',
                            left: depositRequired ? '26px' : '2px',
                            transition: 'left 0.2s'
                          }} />
                        </div>
                      </div>

                      {depositRequired && (
                        <div>
                          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                            {[25, 50, 75].map(pct => (
                              <button
                                key={pct}
                                type="button"
                                onClick={() => setDepositPercentage(pct)}
                                style={{
                                  flex: 1,
                                  padding: '0.5rem',
                                  background: depositPercentage === pct ? colors.accent : colors.bgCard,
                                  border: depositPercentage === pct ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
                                  borderRadius: '6px',
                                  color: depositPercentage === pct ? '#fff' : colors.text,
                                  cursor: 'pointer',
                                  fontWeight: depositPercentage === pct ? 'bold' : 'normal'
                                }}
                              >
                                {pct}%
                              </button>
                            ))}
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '0.75rem',
                            background: `${colors.success}20`,
                            borderRadius: '6px',
                            border: `1px solid ${colors.success}`
                          }}>
                            <span style={{ color: colors.text }}>Deposit Required:</span>
                            <span style={{ color: colors.success, fontWeight: '700' }}>
                              ${(total * (depositPercentage / 100)).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Recurring Service Toggle */}
              <div style={{
                background: colors.bgCard,
                borderRadius: '8px',
                padding: '1rem',
                border: `1px solid ${isRecurring ? colors.info : colors.border}`
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: isRecurring ? '1rem' : 0
                }}>
                  <span style={{ color: colors.text, fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Calendar size={18} />
                    Recurring Service
                  </span>
                  <button
                    onClick={() => setIsRecurring(!isRecurring)}
                    style={{
                      width: '52px',
                      height: '28px',
                      borderRadius: '14px',
                      background: isRecurring ? colors.info : colors.border,
                      border: 'none',
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'background 0.2s'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '2px',
                      left: isRecurring ? '26px' : '2px',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: 'white',
                      transition: 'left 0.2s'
                    }} />
                  </button>
                </div>
                {isRecurring && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                      Frequency
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                      {[
                        { value: 'weekly', label: 'Weekly' },
                        { value: 'biweekly', label: 'Every 2 Weeks' },
                        { value: 'monthly', label: 'Monthly' },
                        { value: 'quarterly', label: 'Quarterly' }
                      ].map(freq => (
                        <button
                          key={freq.value}
                          type="button"
                          onClick={() => setRecurrenceRule(freq.value)}
                          style={{
                            flex: '1 1 80px',
                            padding: '0.5rem',
                            background: recurrenceRule === freq.value ? colors.info : colors.bgInput,
                            border: recurrenceRule === freq.value ? `2px solid ${colors.info}` : `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: recurrenceRule === freq.value ? '#fff' : colors.text,
                            fontSize: '0.8rem',
                            fontWeight: recurrenceRule === freq.value ? 'bold' : 'normal',
                            cursor: 'pointer'
                          }}
                        >
                          {freq.label}
                        </button>
                      ))}
                    </div>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                        End Date (Optional)
                      </label>
                      <input
                        type="date"
                        value={recurrenceEndDate}
                        onChange={(e) => setRecurrenceEndDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        style={{
                          width: '100%',
                          padding: '0.5rem 0.75rem',
                          background: colors.bgInput,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '6px',
                          color: colors.text,
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                    <div style={{
                      padding: '0.75rem',
                      background: `${colors.info}20`,
                      borderRadius: '6px',
                      color: colors.info,
                      fontSize: '0.85rem'
                    }}>
                      Customer will agree to <strong>{recurrenceRule}</strong> recurring service
                      {recurrenceEndDate ? ` until ${new Date(recurrenceEndDate).toLocaleDateString()}` : ' (ongoing)'} when signing.
                    </div>

                    {/* Auto-charge toggle - show if customer has saved card */}
                    {createdCustomer?.default_payment_method_id && createdCustomer?.card_last4 && (
                      <div style={{
                        marginTop: '1rem',
                        padding: '0.75rem',
                        background: autoChargeEnabled ? `${colors.success}15` : colors.bgInput,
                        border: `1px solid ${autoChargeEnabled ? colors.success : colors.border}`,
                        borderRadius: '6px'
                      }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          cursor: 'pointer'
                        }}>
                          <input
                            type="checkbox"
                            checked={autoChargeEnabled}
                            onChange={(e) => setAutoChargeEnabled(e.target.checked)}
                            style={{
                              width: '18px',
                              height: '18px',
                              accentColor: colors.success,
                              cursor: 'pointer'
                            }}
                          />
                          <span style={{ color: colors.text, fontSize: '0.9rem' }}>
                            Auto-charge {createdCustomer.card_brand?.toUpperCase() || 'card'} •••• {createdCustomer.card_last4} on completion
                          </span>
                        </label>
                        {autoChargeEnabled && (
                          <div style={{
                            marginTop: '0.5rem',
                            paddingLeft: '2rem',
                            color: colors.textMuted,
                            fontSize: '0.8rem'
                          }}>
                            Card will be charged automatically when each job is completed. Receipt sent by email.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special notes about this job..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '0.95rem',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
                {/* Primary Actions Row */}
                <div style={{ display: 'flex', gap: '0.5rem', flexDirection: isMobile ? 'column' : 'row' }}>
                  <button
                    onClick={handleQuoteNext}
                    disabled={quoteItems.length === 0}
                    style={{
                      flex: 1,
                      padding: isMobile ? '0.875rem' : '1rem',
                      background: quoteItems.length === 0 ? colors.border : colors.success,
                      border: 'none',
                      borderRadius: '8px',
                      color: quoteItems.length === 0 ? '#666' : colors.bgCard,
                      fontSize: isMobile ? '0.95rem' : '1rem',
                      fontWeight: '700',
                      cursor: quoteItems.length === 0 ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <CheckCircle size={isMobile ? 16 : 18} /> Sign Now
                  </button>
                  <button
                    onClick={handleSaveAndSend}
                    disabled={quoteItems.length === 0}
                    style={{
                      flex: 1,
                      padding: isMobile ? '0.875rem' : '1rem',
                      background: quoteItems.length === 0 ? colors.border : (colors.info || '#3498db'),
                      border: 'none',
                      borderRadius: '8px',
                      color: quoteItems.length === 0 ? '#666' : '#fff',
                      fontSize: isMobile ? '0.95rem' : '1rem',
                      fontWeight: '700',
                      cursor: quoteItems.length === 0 ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Send size={isMobile ? 16 : 18} /> Send to Customer
                  </button>
                </div>

                {/* Secondary Actions Row */}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => {
                      setWorkflowType(null);
                      setStep(2);
                    }}
                    style={{
                      flex: 1,
                      padding: isMobile ? '0.75rem' : '0.875rem',
                      background: 'transparent',
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.textMuted,
                      fontSize: isMobile ? '0.85rem' : '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <ArrowLeft size={isMobile ? 14 : 16} /> Back
                  </button>
                  <button
                    onClick={handleSaveAsDraft}
                    disabled={quoteItems.length === 0}
                    style={{
                      flex: 1,
                      padding: isMobile ? '0.75rem' : '0.875rem',
                      background: 'transparent',
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: quoteItems.length === 0 ? colors.textMuted : colors.text,
                      fontSize: isMobile ? '0.85rem' : '0.9rem',
                      fontWeight: '600',
                      cursor: quoteItems.length === 0 ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Save size={isMobile ? 14 : 16} /> Save Draft
                  </button>
                  <button
                    onClick={onClose}
                    style={{
                      flex: 1,
                      padding: isMobile ? '0.75rem' : '0.875rem',
                      background: 'transparent',
                      border: `1px solid ${colors.error || '#ef4444'}`,
                      borderRadius: '8px',
                      color: colors.error || '#ef4444',
                      fontSize: isMobile ? '0.85rem' : '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP: Schedule Job - Step 3 for quick, Step 4 for full */}
          {((step === 3 && workflowType === 'quick') || (step === 4 && workflowType === 'full')) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '1.25rem' : '2rem' }}>
              {/* Success Message */}
              <div style={{
                background: `linear-gradient(135deg, ${colors.success} 0%, ${colors.info} 100%)`,
                padding: isMobile ? '1rem' : '1.5rem',
                borderRadius: '12px',
                textAlign: 'center',
                color: '#000'
              }}>
                <CheckCircle size={isMobile ? 36 : 48} style={{ marginBottom: isMobile ? '0.5rem' : '1rem' }} />
                <h3 style={{ margin: '0 0 0.25rem 0', fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
                  {workflowType === 'quick' ? 'Customer Created!' : 'Quote Signed!'}
                </h3>
                <p style={{ margin: 0, fontSize: isMobile ? '0.9rem' : '1rem', opacity: 0.9 }}>
                  {workflowType === 'quick' ? 'Now schedule your visit' : 'Now choose when to schedule this job'}
                </p>
              </div>

              {/* Tech Selection - Required for office/admin, optional for techs/owners */}
              {technicians.length > 0 && (
                <div style={{
                  background: colors.bgInput,
                  padding: isMobile ? '1rem' : '1.5rem',
                  borderRadius: '12px',
                  border: needsTechSelection && !selectedTech ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
                  marginBottom: '1rem'
                }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: colors.text,
                    fontWeight: 600,
                    fontSize: isMobile ? '0.95rem' : '1rem'
                  }}>
                    <User size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                    Assign to Technician {needsTechSelection && <span style={{ color: colors.accent }}>*</span>}
                  </label>
                  <select
                    value={selectedTech}
                    onChange={(e) => setSelectedTech(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem 2.25rem 0.75rem 0.75rem',
                      background: `${colors.bgCard} url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>") right 0.75rem center / 14px no-repeat`,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontWeight: '600',
                      fontSize: '1rem',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none'
                    }}
                  >
                    <option value="">{needsTechSelection ? 'Select a technician...' : 'Unassigned (assign later)'}</option>
                    {technicians.map(tech => (
                      <option key={tech.id} value={tech.id}>
                        {tech.name} {tech.id === currentUserId ? '(You)' : ''}
                      </option>
                    ))}
                  </select>
                  {selectedTech === currentUserId && (
                    <p style={{ margin: '0.4rem 0 0 0', fontSize: '0.8rem', color: colors.textMuted }}>
                      Auto-assigned to you — change above if this should go to someone else
                    </p>
                  )}
                  {needsTechSelection && !selectedTech && (
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: colors.accent }}>
                      As office staff, you must assign this job to a technician
                    </p>
                  )}
                </div>
              )}

              {/* Scheduling Choice - Only show for full workflow (jobs that are ready now) */}
              {workflowType === 'full' && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: isMobile ? '1rem' : '1.5rem'
                }}>
                  {/* Do Now */}
                  <div
                    onClick={handleDoNow}
                    style={{
                      background: colors.accent,
                      padding: isMobile ? '1.25rem' : '2rem',
                      borderRadius: '12px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      border: '3px solid transparent',
                      transition: 'all 0.2s',
                      color: '#fff',
                      display: isMobile ? 'flex' : 'block',
                      alignItems: 'center',
                      gap: isMobile ? '1rem' : 0
                    }}
                    onMouseOver={(e) => {
                      if (!isMobile) {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(240, 90, 40, 0.4)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isMobile) {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >
                    <Zap size={isMobile ? 40 : 64} style={{ marginBottom: isMobile ? 0 : '1rem', flexShrink: 0 }} />
                    <div style={{ textAlign: isMobile ? 'left' : 'center' }}>
                      <h3 style={{ margin: '0 0 0.25rem 0', fontSize: isMobile ? '1.1rem' : '1.5rem' }}>Do This Job NOW</h3>
                      <p style={{ margin: 0, fontSize: isMobile ? '0.85rem' : '0.95rem', opacity: 0.9 }}>
                        Start work immediately
                      </p>
                    </div>
                  </div>

                  {/* Schedule Later */}
                  <div
                    style={{
                      background: colors.bgInput,
                      padding: isMobile ? '1.25rem' : '2rem',
                      borderRadius: '12px',
                      textAlign: 'center',
                      border: schedulingChoice === 'later' ? `3px solid ${colors.success}` : `3px solid ${colors.border}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: isMobile ? 'flex' : 'block',
                      alignItems: 'center',
                      gap: isMobile ? '1rem' : 0
                    }}
                    onClick={() => setSchedulingChoice('later')}
                    onMouseOver={(e) => {
                      if (!isMobile) {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.borderColor = colors.success;
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isMobile) {
                        e.currentTarget.style.transform = 'scale(1)';
                        if (schedulingChoice !== 'later') {
                          e.currentTarget.style.borderColor = colors.border;
                        }
                      }
                    }}
                  >
                    <Calendar size={isMobile ? 40 : 64} style={{ marginBottom: isMobile ? 0 : '1rem', color: colors.success, flexShrink: 0 }} />
                    <div style={{ textAlign: isMobile ? 'left' : 'center' }}>
                      <h3 style={{ margin: '0 0 0.25rem 0', fontSize: isMobile ? '1.1rem' : '1.5rem', color: colors.text }}>
                        Schedule for Later
                      </h3>
                      <p style={{ margin: 0, fontSize: isMobile ? '0.85rem' : '0.95rem', color: colors.textMuted }}>
                        Pick a date and time
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Schedule Form - Always show for quick visit, show when 'later' selected for full */}
              {(workflowType === 'quick' || schedulingChoice === 'later') && (
                <div style={{
                  background: colors.bgInput,
                  padding: isMobile ? '1rem' : '1.5rem',
                  borderRadius: '12px',
                  border: `1px solid ${colors.border}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {/* Quote Fee Display - Only for quick visit when enabled */}
                  {workflowType === 'quick' && quoteFeeEnabled && (
                    <div style={{
                      padding: '1rem',
                      background: `${colors.accent}15`,
                      borderRadius: '8px',
                      border: `1px solid ${colors.accent}40`
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '0.75rem'
                      }}>
                        <div>
                          <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.25rem' }}>
                            Quote Visit Fee
                          </div>
                          <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                            Waived if customer agrees to work
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ color: colors.accent, fontSize: '1.25rem', fontWeight: 'bold' }}>$</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={quoteFeeAmount}
                            onChange={(e) => setQuoteFeeAmount(parseFloat(e.target.value) || 0)}
                            style={{
                              width: '80px',
                              padding: '0.5rem',
                              background: colors.bgCard,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              color: colors.text,
                              fontSize: '1rem',
                              textAlign: 'right'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Duration Selector - Show before calendar */}
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    background: colors.bgCard,
                    borderRadius: '8px'
                  }}>
                    <span style={{ color: colors.text, fontWeight: '600', fontSize: isMobile ? '0.9rem' : '1rem' }}>
                      <Clock size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                      {workflowType === 'quick' ? 'Visit Duration:' : 'Job Duration:'}
                    </span>
                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                      {[
                        { label: '30m', value: 30 },
                        { label: '1hr', value: 60 },
                        { label: '1.5hr', value: 90 },
                        { label: '2hr', value: 120 },
                        { label: '3hr', value: 180 },
                        { label: '4hr', value: 240 }
                      ].map(d => (
                        <button
                          key={d.value}
                          onClick={() => setEstimatedDuration(d.value)}
                          style={{
                            padding: isMobile ? '0.35rem 0.6rem' : '0.4rem 0.75rem',
                            background: estimatedDuration === d.value ? colors.accent : colors.bgInput,
                            color: estimatedDuration === d.value ? '#fff' : colors.text,
                            border: estimatedDuration === d.value ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            fontSize: isMobile ? '0.8rem' : '0.85rem',
                            fontWeight: estimatedDuration === d.value ? 'bold' : 'normal',
                            cursor: 'pointer'
                          }}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Visual Week Calendar */}
                  <VisualScheduler
                    companyId={companyId}
                    selectedDate={scheduledDate}
                    selectedTime={scheduledTime}
                    onSelectSlot={(date, time) => {
                      setScheduledDate(date);
                      setScheduledTime(time);
                    }}
                    estimatedDuration={estimatedDuration}
                    onDurationChange={setEstimatedDuration}
                    isRecurring={isRecurring}
                    recurrenceRule={recurrenceRule}
                    recurrenceEndDate={recurrenceEndDate}
                    compact={isMobile}
                    slotMinutes={calendarSlotMinutes}
                    showDurationSelector={false}
                  />

                  {/* Selection Summary */}
                  {scheduledDate && scheduledTime && (
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                      alignItems: 'center',
                      padding: '0.75rem',
                      background: `${colors.success}20`,
                      borderRadius: '8px',
                      border: `1px solid ${colors.success}`
                    }}>
                      <CheckCircle size={18} color={colors.success} />
                      <span style={{ color: colors.text, fontWeight: '600' }}>
                        Selected: {new Date(scheduledDate + 'T12:00:00').toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        })} at {scheduledTime}
                      </span>
                      <span style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                        ({estimatedDuration >= 60 ? `${estimatedDuration / 60} hr${estimatedDuration > 60 ? 's' : ''}` : `${estimatedDuration} min`})
                      </span>
                    </div>
                  )}

                  {/* Recurring Options - Only show for full workflow (not appointments) */}
                  {recurringEnabled && workflowType === 'full' && (
                    <div style={{
                      padding: isMobile ? '0.75rem' : '1rem',
                      background: colors.bgCard,
                      borderRadius: '8px',
                      border: isRecurring ? `2px solid #9b59b6` : `1px solid ${colors.border}`
                    }}>
                      {/* Toggle */}
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        marginBottom: isRecurring ? '1rem' : 0
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <RefreshCw size={isMobile ? 16 : 18} color={isRecurring ? '#9b59b6' : colors.textMuted} />
                          <span style={{ color: colors.text, fontWeight: 'bold', fontSize: isMobile ? '0.9rem' : '1rem' }}>
                            Make Recurring
                          </span>
                        </div>
                        <input
                          type="checkbox"
                          checked={isRecurring}
                          onChange={(e) => setIsRecurring(e.target.checked)}
                          style={{
                            width: isMobile ? '40px' : '44px',
                            height: isMobile ? '22px' : '24px',
                            cursor: 'pointer'
                          }}
                        />
                      </label>

                      {/* Recurrence Options */}
                      {isRecurring && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '0.75rem' : '1rem' }}>
                          {/* Recurrence Pattern */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
                            <span style={{ color: colors.text, fontSize: '0.9rem' }}>Repeat:</span>
                            <select
                              value={recurrenceRule}
                              onChange={(e) => setRecurrenceRule(e.target.value)}
                              style={{
                                padding: '0.4rem 0.75rem',
                                background: colors.bgInput,
                                border: `1px solid ${colors.border}`,
                                borderRadius: '6px',
                                color: colors.text,
                                fontSize: '0.9rem'
                              }}
                            >
                              <option value="weekly">Weekly</option>
                              <option value="biweekly">Every 2 Weeks</option>
                              <option value="monthly">Monthly</option>
                            </select>

                            <span style={{ color: colors.text, fontSize: '0.9rem' }}>Until:</span>
                            <input
                              type="date"
                              value={recurrenceEndDate}
                              onChange={(e) => setRecurrenceEndDate(e.target.value)}
                              min={scheduledDate || new Date().toISOString().split('T')[0]}
                              style={{
                                padding: '0.4rem 0.75rem',
                                background: colors.bgInput,
                                border: `1px solid ${colors.border}`,
                                borderRadius: '6px',
                                color: colors.text,
                                fontSize: '0.9rem'
                              }}
                            />
                          </div>

                          {/* Preview */}
                          {scheduledDate && recurrenceEndDate && (
                            <div style={{
                              padding: '0.5rem 0.75rem',
                              background: '#9b59b620',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              color: '#9b59b6'
                            }}>
                              Repeats {recurrenceRule} until {new Date(recurrenceEndDate + 'T12:00:00').toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    onClick={handleScheduleLater}
                    disabled={creatingJob || !scheduledDate || (isRecurring && !recurrenceEndDate)}
                    style={{
                      padding: '1rem',
                      background: creatingJob ? colors.border : (scheduledDate && (!isRecurring || recurrenceEndDate)) ? colors.success : colors.border,
                      border: 'none',
                      borderRadius: '8px',
                      color: creatingJob ? '#666' : (scheduledDate && (!isRecurring || recurrenceEndDate)) ? colors.bgCard : '#666',
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      cursor: (creatingJob || !scheduledDate || (isRecurring && !recurrenceEndDate)) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <CheckCircle size={20} />
                    {creatingJob ? 'Scheduling...' : isRecurring ? 'Confirm Recurring Schedule' : 'Confirm Schedule'}
                  </button>

                  {/* Back and Cancel buttons */}
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                    <button
                      onClick={() => {
                        if (workflowType === 'quick') {
                          setWorkflowType(null);
                          setStep(2);
                          // Reset scheduling state for clean re-entry
                          setScheduledDate('');
                          setScheduledTime('9:00');
                          setAvailableTimeSlots([]);
                        } else {
                          setStep(3);
                        }
                        setSchedulingChoice(null);
                      }}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: 'transparent',
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        color: colors.textMuted,
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <ArrowLeft size={16} /> Back
                    </button>
                    <button
                      onClick={onClose}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: 'transparent',
                        border: `1px solid ${colors.error || '#ef4444'}`,
                        borderRadius: '8px',
                        color: colors.error || '#ef4444',
                        fontSize: '0.9rem',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Send Quote Modal */}
      {showSendModal && createdQuote && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: isMobile ? '1.5rem' : '2rem',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: `2px solid ${colors.info || '#3498db'}`
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: `${colors.info || '#3498db'}30`,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Send size={20} style={{ color: colors.info || '#3498db' }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <h3 style={{ margin: 0, color: colors.text, fontSize: '1.1rem' }}>
                  Send Quote to Customer
                </h3>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  They can review and sign remotely
                </div>
              </div>
              <button
                onClick={() => setShowSendModal(false)}
                style={{
                  marginLeft: 'auto',
                  background: 'transparent',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Customer Info - Editable */}
            {createdCustomer && (
              <div style={{
                background: colors.bgInput,
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <div style={{ color: colors.text, fontWeight: 'bold' }}>
                    {createdCustomer.name}
                  </div>
                  <button
                    onClick={() => setEditingContactInfo(!editingContactInfo)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: colors.info || '#3498db',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <Edit3 size={14} />
                    {editingContactInfo ? 'Done' : 'Edit'}
                  </button>
                </div>

                {editingContactInfo ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div>
                      <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                        Email
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Mail size={14} style={{ color: colors.textMuted }} />
                        <input
                          type="email"
                          value={sendModalEmail}
                          onChange={(e) => setSendModalEmail(e.target.value)}
                          placeholder="customer@email.com"
                          style={{
                            flex: 1,
                            padding: '0.5rem',
                            background: colors.bgCard,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.9rem'
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                        Phone
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <MessageSquare size={14} style={{ color: colors.textMuted }} />
                        <input
                          type="tel"
                          value={sendModalPhone}
                          onChange={(e) => setSendModalPhone(e.target.value)}
                          placeholder="555-123-4567"
                          style={{
                            flex: 1,
                            padding: '0.5rem',
                            background: colors.bgCard,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.9rem'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ color: colors.textMuted, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Mail size={14} /> {sendModalEmail || <span style={{ fontStyle: 'italic' }}>No email</span>}
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                      <MessageSquare size={14} /> {sendModalPhone || <span style={{ fontStyle: 'italic' }}>No phone</span>}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Additional Recipients */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <label style={{
                  color: colors.text,
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  Additional Recipients
                </label>
                <button
                  onClick={() => setAdditionalRecipients([...additionalRecipients, { email: '', phone: '' }])}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${colors.border}`,
                    color: colors.text,
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    padding: '0.35rem 0.75rem',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                >
                  <UserPlus size={14} />
                  Add
                </button>
              </div>

              {additionalRecipients.length === 0 ? (
                <div style={{ color: colors.textMuted, fontSize: '0.85rem', fontStyle: 'italic' }}>
                  No additional recipients
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {additionalRecipients.map((recipient, idx) => (
                    <div key={idx} style={{
                      background: colors.bgInput,
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: `1px solid ${colors.border}`
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <span style={{ color: colors.textMuted, fontSize: '0.8rem' }}>Recipient {idx + 2}</span>
                        <button
                          onClick={() => setAdditionalRecipients(additionalRecipients.filter((_, i) => i !== idx))}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: colors.danger || '#e74c3c',
                            cursor: 'pointer',
                            padding: '0.25rem'
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 150px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Mail size={12} style={{ color: colors.textMuted }} />
                            <input
                              type="email"
                              value={recipient.email}
                              onChange={(e) => {
                                const updated = [...additionalRecipients];
                                updated[idx].email = e.target.value;
                                setAdditionalRecipients(updated);
                              }}
                              placeholder="Email"
                              style={{
                                flex: 1,
                                padding: '0.4rem',
                                background: colors.bgCard,
                                border: `1px solid ${colors.border}`,
                                borderRadius: '4px',
                                color: colors.text,
                                fontSize: '0.85rem'
                              }}
                            />
                          </div>
                        </div>
                        <div style={{ flex: '1 1 150px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <MessageSquare size={12} style={{ color: colors.textMuted }} />
                            <input
                              type="tel"
                              value={recipient.phone}
                              onChange={(e) => {
                                const updated = [...additionalRecipients];
                                updated[idx].phone = e.target.value;
                                setAdditionalRecipients(updated);
                              }}
                              placeholder="Phone"
                              style={{
                                flex: 1,
                                padding: '0.4rem',
                                background: colors.bgCard,
                                border: `1px solid ${colors.border}`,
                                borderRadius: '4px',
                                color: colors.text,
                                fontSize: '0.85rem'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quote Link */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: colors.text,
                fontWeight: 'bold',
                fontSize: '0.9rem'
              }}>
                Quote Link
              </label>
              <div style={{
                display: 'flex',
                gap: '0.5rem'
              }}>
                <input
                  type="text"
                  readOnly
                  value={getQuoteUrl(createdQuote.id)}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.textMuted,
                    fontSize: '0.85rem'
                  }}
                />
                <button
                  onClick={copyQuoteLink}
                  style={{
                    padding: '0.75rem 1rem',
                    background: quoteLinkCopied ? colors.success : colors.bgInput,
                    border: `1px solid ${quoteLinkCopied ? colors.success : colors.border}`,
                    borderRadius: '8px',
                    color: quoteLinkCopied ? '#fff' : colors.text,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {quoteLinkCopied ? <CheckCircle size={16} /> : <Copy size={16} />}
                  {quoteLinkCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Send Method */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.75rem',
                color: colors.text,
                fontWeight: 'bold',
                fontSize: '0.9rem'
              }}>
                Send via
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[
                  { value: 'email', label: 'Email', icon: Mail },
                  { value: 'sms', label: 'SMS', icon: MessageSquare },
                  { value: 'both', label: 'Both', icon: Send }
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => setSendMethod(option.value)}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: sendMethod === option.value ? `${colors.info || '#3498db'}20` : colors.bgInput,
                      border: `2px solid ${sendMethod === option.value ? colors.info || '#3498db' : colors.border}`,
                      borderRadius: '8px',
                      color: sendMethod === option.value ? colors.info || '#3498db' : colors.text,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      fontWeight: sendMethod === option.value ? 'bold' : 'normal',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <option.icon size={16} />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => setShowSendModal(false)}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: colors.border,
                  border: 'none',
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  minHeight: '48px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendQuote}
                disabled={sendingQuote}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: sendingQuote ? colors.border : `linear-gradient(135deg, ${colors.info || '#3498db'} 0%, #2980b9 100%)`,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: sendingQuote ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  minHeight: '48px',
                  opacity: sendingQuote ? 0.7 : 1
                }}
              >
                {sendingQuote ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Quote
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
// Force rebuild 1768117286
